import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '@env/environment';

export interface OrganizationUnitEventTypeDto {
  value: number;
  name: string;
  description: string;
  sortOrder: number;
}

export interface CreateOrganizationUnitEventTypeDto {
  name: string;
  description?: string | null;
  sortOrder?: number | null;
}

export interface UpdateOrganizationUnitEventTypeDto {
  name: string;
  description?: string | null;
  sortOrder?: number | null;
}

type OrganizationUnitEventTypeApiDto = OrganizationUnitEventTypeDto & {
  displayName?: string;
};

type OrganizationUnitEventTypeWriteDto = CreateOrganizationUnitEventTypeDto & {
  displayName: string;
};

@Injectable({ providedIn: 'root' })
export class OrganizationUnitEventTypeManagementService {
  private readonly apiUrl = `${environment.apiUrl}/organization-unit-event-types`;

  constructor(private readonly http: HttpClient) {}

  getAll(): Observable<OrganizationUnitEventTypeDto[]> {
    return this.http.get<OrganizationUnitEventTypeApiDto[]>(this.apiUrl).pipe(
      map(types => types.map(type => this.normalize(type))),
    );
  }

  getById(id: number): Observable<OrganizationUnitEventTypeDto> {
    return this.http.get<OrganizationUnitEventTypeApiDto>(`${this.apiUrl}/${id}`).pipe(
      map(type => this.normalize(type)),
    );
  }

  create(dto: CreateOrganizationUnitEventTypeDto): Observable<OrganizationUnitEventTypeDto> {
    return this.http.post<OrganizationUnitEventTypeApiDto>(this.apiUrl, this.toWriteDto(dto)).pipe(
      map(type => this.normalize(type)),
    );
  }

  update(id: number, dto: UpdateOrganizationUnitEventTypeDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}`, this.toWriteDto(dto));
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  updateSortOrder(ids: number[]): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/sort-order`, { ids });
  }

  private normalize(dto: OrganizationUnitEventTypeApiDto): OrganizationUnitEventTypeDto {
    return {
      value: dto.value,
      name: dto.name,
      description: dto.description ?? dto.displayName ?? '',
      sortOrder: dto.sortOrder,
    };
  }

  private toWriteDto(dto: CreateOrganizationUnitEventTypeDto): OrganizationUnitEventTypeWriteDto {
    return {
      ...dto,
      displayName: dto.description ?? '',
    };
  }
}
